import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';

import { Plus, Trash } from 'lucide-react';
import {
  Button,
  ButtonIcon,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Select,
  Separator,
  Slider,
  Switch,
} from 'twyne-new.ui';

import { useRevenueIntegrationOptions } from '@Components/rules/options';
import InfoTip from 'components/InfoTip';

const propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

let uid = 0

const emptyRow = () => ({ key: ++uid, id: null, value: 0 })

const parseId = id => Number.isNaN(Number(id)) ? id : Number(id)

const parseRows = value => Object.entries(value || {})
  .map(([id, percent]) => ({ key: ++uid, id: parseId(id), value: Number(percent) || 0 }))

const parseValue = rows => rows.reduce((integrations, { id, value }) => {
  if (id) {
    integrations[id] = Number(value) || 0
  }
  return integrations
}, {})

export default function RevenueIntegrations({ value, onChange, disabled }) {
  const options = useRevenueIntegrationOptions() || []
  const [rows, setRows] = useState(() => parseRows(value))
  const [enabled, setEnabled] = useState(() => Object.keys(value || {}).length > 0)

  const updateRows = useCallback(rows => {
    setRows(rows)
    onChange(parseValue(rows))
  }, [onChange])

  const handleToggle = useCallback(({ target }) => {
    setEnabled(target.checked)

    if (target.checked) {
      updateRows(rows.length ? rows : [emptyRow()])
    } else {
      onChange({})
    }
  }, [rows, updateRows, onChange])

  const handleChangeRow = useCallback((index, row) => {
    updateRows(rows.map((current, rowIndex) => rowIndex === index ? row : current))
  }, [rows, updateRows])

  const handleAddRow = useCallback(() => {
    updateRows([...rows, emptyRow()])
  }, [rows, updateRows])

  const handleDeleteRow = useCallback(index => {
    updateRows(rows.filter((_, rowIndex) => rowIndex !== index))
  }, [rows, updateRows])

  return <Card>
    <CardHeader>
      <CardTitle className='flex items-center gap-1'>
        Revenue Discount
        <InfoTip id='revenue-integrations' color='neutral' disabled>
          You can change the value we consider to add bucket. If the bucket will receive 10 dollars and the integration was configured to discount 60%, the bucket will receive only 4 dollars.
        </InfoTip>
      </CardTitle>
      <Switch
        name='revenue-discount-enabled'
        value={enabled}
        onChange={handleToggle}
        disabled={disabled}
      />
    </CardHeader>

    {enabled ? <>
      <Separator className='my-4' />

      <CardContent>
        <div className='flex justify-between items-center mb-4'>
          <Label className='font-semibold twy-text-base-accent whitespace-nowrap'>
            Revenue by integration
          </Label>
          <Button color='accent' size='sm' onClick={handleAddRow} disabled={disabled}>
            <Plus size={14} />
            Add Integration
          </Button>
        </div>

        <div className='flex flex-col gap-y-3'>
          {rows.map((row, index) => <Item
            key={row.key}
            data={row}
            options={options}
            selected={rows.filter((_, rowIndex) => rowIndex !== index).map(({ id }) => id)}
            onChange={updated => handleChangeRow(index, updated)}
            onDeleteRow={() => handleDeleteRow(index)}
            disabled={disabled}
          />)}
        </div>

        {rows.length ? null : <div className='rounded-xs border border-dashed twy-border-base px-4 py-6 text-center'>
          <small className='twy-text-base-neutral'>
            No integration added yet.
          </small>
        </div>}
      </CardContent>
    </> : null}
  </Card>
}

function Item({ data, options, selected, onChange, onDeleteRow, disabled }) {
  const { id, value } = data

  const integrationOptions = useMemo(() => options.map(option => ({
    ...option,
    disabled: option.value !== id && selected.includes(option.value),
  })), [options, selected, id])

  return <div className='rounded-xs border twy-border-base twy-bg-base'>
    <div className='flex items-center gap-4 px-4 py-3'>
      <Label className='w-28 shrink-0 twy-text-base-neutral'>Integration</Label>
      <div className='w-full'>
        <Select
          name={`revenue-integration-${id}`}
          value={id}
          options={integrationOptions}
          clearable={false}
          disabled={disabled}
          onChange={option => onChange({ ...data, id: option ? option.value : null })}
        />
      </div>
      <ButtonIcon content='Delete' onClick={onDeleteRow} disabled={disabled}>
        <Trash size={16} />
      </ButtonIcon>
    </div>

    <Separator />

    <div className='px-4 py-3'>
      <div className='flex items-center gap-4'>
        <Label className='w-28 shrink-0 twy-text-base-neutral'>Discount</Label>
        <Slider
          name={`revenue-discount-${id}`}
          min={0}
          max={100}
          step={1}
          value={Number(value) || 0}
          disabled={disabled}
          onChange={percent => onChange({ ...data, value: percent })}
        />
        <span className='w-12 shrink-0 text-right twy-text-base-neutral'>
          {Number(value) || 0}%
        </span>
      </div>
      <Label className='mt-2 pl-32'>
        <small>$Revenue &#215; (1 &#8722; discount<b>%</b>) &#61; <b>$</b>bucket credit</small>
      </Label>
    </div>
  </div>
}

Item.propTypes = {
  data: PropTypes.object,
  options: PropTypes.array,
  selected: PropTypes.array,
  onChange: PropTypes.func,
  onDeleteRow: PropTypes.func,
  disabled: PropTypes.bool,
}

RevenueIntegrations.propTypes = propTypes
RevenueIntegrations.defaultProps = {
  value: {},
  disabled: false,
}
